import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import axios from 'axios'
import Select from 'react-select'
import {Overlay, OverlayTrigger, Popover, Tooltip} from 'react-bootstrap'
import toaster from '../../../../../Utils/toaster'
import CallTagColorPicker, {contrastTextForHex} from './CallTagColorPicker'

const API_URL = process.env.REACT_APP_API_URL
const MAX_TAGS_PER_CALL = 3
const EXISTING_TAGS_PREVIEW = 5
const PILL_RADIUS = '999px'

export type CallTag = {
  id: number
  name: string
  color: string
  isSystem: boolean
  usageCount?: number
}

type TagOption = {
  value: number
  label: string
  color: string
  isSystem: boolean
  usageCount: number
}

type Props = {
  callHeaderCode: number
  enabled: boolean
  onTagsChange?: (tags: CallTag[]) => void
}

function normalizeHex(raw: string): string {
  let v = (raw || '').trim()
  if (!v) return '#6B7280'
  if (!v.startsWith('#')) v = `#${v}`
  if (/^#[0-9A-Fa-f]{3}$/.test(v)) {
    const r = v[1]
    const g = v[2]
    const b = v[3]
    v = `#${r}${r}${g}${g}${b}${b}`
  }
  if (!/^#[0-9A-Fa-f]{6}$/.test(v)) return '#6B7280'
  return v.toUpperCase()
}

function normalizeTag(raw: any): CallTag {
  return {
    id: Number(raw?.id ?? raw?.Id ?? 0),
    name: String(raw?.name ?? raw?.Name ?? ''),
    color: normalizeHex(String(raw?.color ?? raw?.Color ?? '#6B7280')),
    isSystem: Boolean(raw?.isSystem ?? raw?.IsSystem ?? false),
    usageCount: Number(raw?.usageCount ?? raw?.UsageCount ?? 0),
  }
}

function TagPill({
  tag,
  onRemove,
  onAssign,
  removeIcon = 'x',
  compact = false,
  disabled,
}: {
  tag: CallTag
  onRemove?: () => void
  onAssign?: () => void
  removeIcon?: 'x' | 'trash'
  compact?: boolean
  disabled?: boolean
}) {
  const bg = normalizeHex(tag.color)
  const fg = contrastTextForHex(bg)
  const btnSize = compact ? 17 : 21
  return (
    <span
      className='d-inline-flex align-items-center'
      style={{
        backgroundColor: bg,
        color: fg,
        borderRadius: PILL_RADIUS,
        border: '1px solid rgba(0,0,0,0.12)',
        padding: compact ? '3px 7px' : '4px 9px',
        fontSize: compact ? '0.735rem' : '0.7875rem',
        fontWeight: 600,
        gap: compact ? 4 : 6,
        maxWidth: 189,
        lineHeight: 1.2,
      }}
    >
      <span className='text-truncate' style={{fontWeight: 600}}>{tag.name}</span>
      {onAssign && (
        <button
          type='button'
          className='btn btn-icon btn-sm p-0'
          style={{
            width: btnSize,
            height: btnSize,
            color: '#FFFFFF',
            opacity: 0.95,
            backgroundColor: 'rgba(17,17,17,0.25)',
            borderRadius: '50%',
            lineHeight: 1,
          }}
          onClick={(e) => {
            e.stopPropagation()
            onAssign()
          }}
          disabled={disabled}
          aria-label={`Assign ${tag.name}`}
          title='Assign to this call'
        >
          <i className='bi bi-plus' style={{fontSize: compact ? 12 : 14, color: '#FFFFFF'}} />
        </button>
      )}
      {onRemove && (
        <button
          type='button'
          className='btn btn-icon btn-sm p-0'
          style={{
            width: btnSize,
            height: btnSize,
            color: '#FFFFFF',
            opacity: 0.95,
            backgroundColor: 'rgba(17,17,17,0.25)',
            borderRadius: '50%',
            lineHeight: 1,
          }}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          disabled={disabled}
          aria-label={removeIcon === 'trash' ? `Delete ${tag.name}` : `Remove ${tag.name}`}
          title={removeIcon === 'trash' ? 'Delete tag permanently' : 'Remove from call'}
        >
          {removeIcon === 'trash' ? (
            <i className='bi bi-trash' style={{fontSize: compact ? 9 : 12, color: '#FFFFFF'}} />
          ) : (
            <span style={{fontSize: compact ? 13 : 15, lineHeight: 1, color: '#FFFFFF'}}>×</span>
          )}
        </button>
      )}
    </span>
  )
}

const CallTagsSection: React.FC<Props> = ({callHeaderCode, enabled, onTagsChange}) => {
  const [catalog, setCatalog] = useState<CallTag[]>([])
  const [assigned, setAssigned] = useState<CallTag[]>([])
  const [showManage, setShowManage] = useState(false)
  const [showMoreTags, setShowMoreTags] = useState(false)
  const [moreSearch, setMoreSearch] = useState('')
  const [newTagName, setNewTagName] = useState('')
  const [newTagColor, setNewTagColor] = useState('#FAC81C')
  const [saving, setSaving] = useState(false)
  const newTagInputRef = useRef<HTMLInputElement | null>(null)
  const manageBtnRef = useRef<HTMLButtonElement | null>(null)
  const moreBtnRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!showManage) {
      setShowMoreTags(false)
      return
    }

    const parentModal = document.getElementById('kt_call_in_details') as HTMLElement | null
    const bootstrapModal = (window as any).bootstrap?.Modal?.getInstance?.(parentModal)
    const prevFocus = bootstrapModal?._config?.focus
    if (bootstrapModal?._config) {
      bootstrapModal._config.focus = false
    }

    const blockParentFocusTrap = (event: FocusEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (
        panelRef.current?.contains(target) ||
        document.querySelector('.call-tags-more-picker')?.contains(target) ||
        document.querySelector('.call-tag-color-picker')?.contains(target)
      ) {
        event.stopImmediatePropagation()
      }
    }
    document.addEventListener('focusin', blockParentFocusTrap, true)

    const onDocDown = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (!target) return
      if (manageBtnRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      if (document.querySelector('.call-tags-more-picker')?.contains(target)) return
      if (document.querySelector('.call-tag-color-picker')?.contains(target)) return
      setShowManage(false)
      setShowMoreTags(false)
    }
    document.addEventListener('mousedown', onDocDown)

    const t = window.setTimeout(() => newTagInputRef.current?.focus(), 50)

    return () => {
      window.clearTimeout(t)
      document.removeEventListener('focusin', blockParentFocusTrap, true)
      document.removeEventListener('mousedown', onDocDown)
      if (bootstrapModal?._config && prevFocus !== undefined) {
        bootstrapModal._config.focus = prevFocus
      }
    }
  }, [showManage])

  const notify = useCallback(
    (tags: CallTag[]) => {
      onTagsChange?.(tags)
    },
    [onTagsChange]
  )

  const loadCatalog = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/call-tags`)
      const list = (res?.data?.result ?? res?.data?.Result ?? []).map(normalizeTag)
      setCatalog(list)
    } catch (e: any) {
      console.error(e)
    }
  }, [])

  const loadAssigned = useCallback(async () => {
    if (!callHeaderCode || callHeaderCode <= 0) {
      setAssigned([])
      notify([])
      return
    }
    try {
      const res = await axios.get(`${API_URL}/call-tags/by-call/${callHeaderCode}`)
      const list = (res?.data?.result ?? res?.data?.Result ?? []).map(normalizeTag)
      setAssigned(list)
      notify(list)
    } catch (e: any) {
      console.error(e)
      setAssigned([])
      notify([])
    }
  }, [callHeaderCode, notify])

  useEffect(() => {
    if (!enabled) return
    loadCatalog()
    loadAssigned()
  }, [enabled, callHeaderCode, loadCatalog, loadAssigned])

  const options: TagOption[] = useMemo(
    () =>
      catalog.map((t) => ({
        value: t.id,
        label: t.name,
        color: t.color,
        isSystem: t.isSystem,
        usageCount: t.usageCount ?? 0,
      })),
    [catalog]
  )

  const selectedOptions = useMemo(
    () => options.filter((o) => assigned.some((a) => a.id === o.value)),
    [options, assigned]
  )

  // Catalog is sorted by usage desc from API — show last 5 = least among top? User said "last 5 tags"
  // Interpret as first 5 most-used (preview), same as before but count=5. "last 5" might mean most recent —
  // API sorts by usage. Keeping first 5 most-used as preview.
  const topExisting = useMemo(() => catalog.slice(0, EXISTING_TAGS_PREVIEW), [catalog])
  const hasMoreExisting = catalog.length > EXISTING_TAGS_PREVIEW

  const filteredMoreTags = useMemo(() => {
    const q = moreSearch.trim().toLowerCase()
    if (!q) return catalog
    return catalog.filter((t) => t.name.toLowerCase().includes(q))
  }, [catalog, moreSearch])

  const nameExists = useMemo(() => {
    const n = newTagName.trim().toLowerCase()
    if (!n) return false
    return catalog.some((t) => t.name.toLowerCase() === n)
  }, [catalog, newTagName])

  const atMax = assigned.length >= MAX_TAGS_PER_CALL
  const assignedIds = useMemo(() => new Set(assigned.map((t) => t.id)), [assigned])

  const persistAssignments = async (tagIds: number[]) => {
    if (!callHeaderCode) return
    setSaving(true)
    try {
      const uniqueIds = Array.from(new Set(tagIds)).slice(0, MAX_TAGS_PER_CALL)
      const res = await axios.put(`${API_URL}/call-tags/by-call/${callHeaderCode}`, {
        tagIds: uniqueIds,
      })
      if ((res?.data?.status ?? res?.data?.Status) === 'E') {
        toaster('error', res?.data?.text ?? res?.data?.Text ?? 'Failed to update tags')
        await loadAssigned()
        return
      }
      const list = (res?.data?.result ?? res?.data?.Result ?? []).map(normalizeTag)
      setAssigned(list)
      notify(list)
      await loadCatalog()
    } catch (e: any) {
      toaster('error', e?.response?.data?.text || e?.message || 'Failed to update tags')
      await loadAssigned()
    } finally {
      setSaving(false)
    }
  }

  const handleSelectChange = (opts: readonly TagOption[] | null) => {
    void persistAssignments((opts ?? []).map((o) => o.value))
  }

  const assignExistingTag = async (tag: CallTag) => {
    if (assignedIds.has(tag.id)) {
      toaster('info', 'Tag already on this call')
      return
    }
    if (atMax) {
      toaster('error', `Maximum ${MAX_TAGS_PER_CALL} tags allowed per call`)
      return
    }
    await persistAssignments([...assigned.map((t) => t.id), tag.id])
  }

  const createAndMaybeAssign = async () => {
    const name = newTagName.trim()
    if (!name || nameExists) return
    const color = normalizeHex(newTagColor)
    setSaving(true)
    try {
      const createRes = await axios.post(`${API_URL}/call-tags`, {name, color})
      if ((createRes?.data?.status ?? createRes?.data?.Status) === 'E') {
        toaster('error', createRes?.data?.text ?? createRes?.data?.Text ?? 'Failed to create tag')
        return
      }
      const created = normalizeTag(createRes?.data?.result ?? createRes?.data?.Result)
      await loadCatalog()
      setNewTagName('')
      if (created.id && assigned.length < MAX_TAGS_PER_CALL) {
        await persistAssignments([...assigned.map((t) => t.id), created.id])
      } else {
        toaster(
          'success',
          assigned.length >= MAX_TAGS_PER_CALL
            ? 'Tag created (call already has 3 tags — assign later after removing one)'
            : 'Tag created'
        )
      }
    } catch (e: any) {
      const status = e?.response?.status
      const msg =
        e?.response?.data?.text ||
        e?.response?.data?.Text ||
        (status === 409 ? 'Tag name already exists' : e?.message) ||
        'Failed to create tag'
      toaster('error', msg)
    } finally {
      setSaving(false)
    }
  }

  const removeAssigned = async (tag: CallTag) => {
    setSaving(true)
    try {
      const res = await axios.delete(`${API_URL}/call-tags/by-call/${callHeaderCode}/${tag.id}`)
      if ((res?.data?.status ?? res?.data?.Status) === 'E') {
        toaster('error', res?.data?.text ?? res?.data?.Text ?? 'Failed to remove tag')
        return
      }
      await loadAssigned()
      await loadCatalog()
    } catch (e: any) {
      toaster('error', e?.response?.data?.text || e?.message || 'Failed to remove tag')
    } finally {
      setSaving(false)
    }
  }

  const deleteCatalogTag = async (tag: CallTag) => {
    if (tag.isSystem) return
    if (!window.confirm(`Delete tag "${tag.name}" from the system? It will be removed from all calls.`)) {
      return
    }
    setSaving(true)
    try {
      const res = await axios.delete(`${API_URL}/call-tags/${tag.id}`)
      if ((res?.data?.status ?? res?.data?.Status) === 'E') {
        toaster('error', res?.data?.text ?? res?.data?.Text ?? 'Failed to delete tag')
        return
      }
      toaster('success', 'Tag deleted')
      await loadCatalog()
      await loadAssigned()
    } catch (e: any) {
      toaster('error', e?.response?.data?.text || e?.message || 'Failed to delete tag')
    } finally {
      setSaving(false)
    }
  }

  if (!enabled || !callHeaderCode) return null

  const formatOptionLabel = (opt: TagOption, meta?: {context?: string}) => {
    const isMenu = meta?.context === 'menu'
    const labelColor = isMenu ? '#181C32' : contrastTextForHex(opt.color)
    return (
      <div className='d-flex align-items-center gap-2'>
        <span
          className='d-inline-block'
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            backgroundColor: opt.color,
            flexShrink: 0,
          }}
        />
        <span style={{color: labelColor, fontWeight: isMenu ? 400 : 600}}>{opt.label}</span>
      </div>
    )
  }

  // Match compact TagPill styling used inside Manage Tags
  const selectPillStyles = {
    menuPortal: (base: any) => ({...base, zIndex: 10050}),
    menu: (base: any) => ({
      ...base,
      zIndex: 10050,
      marginBottom: 4,
    }),
    menuList: (base: any) => ({
      ...base,
      maxHeight: 220,
    }),
    option: (base: any, state: any) => ({
      ...base,
      color: '#181C32',
      backgroundColor: state.isFocused ? '#F1F1F2' : '#FFFFFF',
      cursor: 'pointer',
    }),
    singleValue: (base: any) => ({...base, color: '#181C32'}),
    input: (base: any) => ({...base, color: '#181C32'}),
    placeholder: (base: any) => ({...base, color: '#A1A5B7'}),
    multiValue: (base: any, state: any) => ({
      ...base,
      backgroundColor: normalizeHex(state.data.color),
      borderRadius: PILL_RADIUS,
      border: '1px solid rgba(0,0,0,0.12)',
      margin: '2px 3px',
      minHeight: 0,
      alignItems: 'center',
    }),
    multiValueLabel: (base: any, state: any) => ({
      ...base,
      color: contrastTextForHex(state.data.color),
      fontWeight: 600,
      fontSize: '0.735rem',
      padding: '3px 4px 3px 7px',
      lineHeight: 1.2,
      letterSpacing: '0.01em',
    }),
    multiValueRemove: (base: any, state: any) => ({
      ...base,
      color: '#FFFFFF',
      borderRadius: PILL_RADIUS,
      paddingLeft: 2,
      paddingRight: 6,
      ':hover': {
        backgroundColor: 'rgba(17,17,17,0.25)',
        color: '#FFFFFF',
      },
    }),
  }

  return (
    <>
      <div className='mb-2 p-4 rounded border border-gray-300 bg-secondary bg-opacity-10'>
        <div className='d-flex justify-content-between align-items-center mb-2 position-relative'>
          <h6 className='text-muted text-uppercase fs-8 fw-bold ls-1 mb-0'>Tags</h6>
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip id='tooltip-manage-call-tags'>
                {showManage ? 'Close' : 'Manage Tags'}
              </Tooltip>
            }
          >
            <button
              ref={manageBtnRef}
              type='button'
              className='btn btn-icon btn-sm btn-light'
              onClick={() => setShowManage((v) => !v)}
              disabled={saving}
              aria-expanded={showManage}
              aria-label={showManage ? 'Close manage tags' : 'Manage tags'}
            >
              <i className={`bi ${showManage ? 'bi-chevron-down' : 'bi-gear'} fs-6`}></i>
            </button>
          </OverlayTrigger>

          {showManage && (
            <div
              ref={panelRef}
              className='call-tags-manage-panel'
              style={{
                position: 'absolute',
                right: 0,
                bottom: 'calc(100% + 10px)',
                width: 414,
                maxWidth: 'min(414px, 94vw)',
                zIndex: 1060,
                backgroundColor: '#F4F5F7',
                border: '1px solid #D8DCE3',
                borderRadius: 12,
                boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
                padding: 16,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className='fw-bold fs-6 mb-3'>Manage Tags</div>
              <p className='text-muted fs-7 mb-3'>
                Assign up to {MAX_TAGS_PER_CALL} tags per call. Create unlimited tags in the catalog.
              </p>

              {/* Group A: Current + Existing */}
              <div
                className='mb-3 p-3 rounded'
                style={{backgroundColor: '#EEF0F3', border: '1px solid #E0E3E8'}}
              >
                <div className='mb-3'>
                  <label className='form-label fs-7 mb-2 fw-semibold text-muted text-uppercase'>
                    Current Tags
                  </label>
                  <div className='d-flex flex-wrap gap-2'>
                    {assigned.map((tag) => (
                      <TagPill
                        key={tag.id}
                        tag={tag}
                        compact
                        removeIcon='x'
                        disabled={saving}
                        onRemove={() => removeAssigned(tag)}
                      />
                    ))}
                    {assigned.length === 0 && (
                      <span className='text-muted fs-7'>No tags on this call</span>
                    )}
                  </div>
                  {atMax && (
                    <small className='text-muted d-block mt-2'>
                      Max {MAX_TAGS_PER_CALL} reached — remove one to assign another.
                    </small>
                  )}
                </div>

                <div>
                  <label className='form-label fs-7 mb-2 fw-semibold text-muted text-uppercase'>
                    Call Tags Catalog
                  </label>
                  <div className='d-flex flex-wrap align-items-center gap-2'>
                    {topExisting.map((tag) => (
                      <TagPill
                        key={tag.id}
                        tag={tag}
                        compact
                        removeIcon='trash'
                        disabled={saving}
                        onAssign={
                          assignedIds.has(tag.id) ? undefined : () => assignExistingTag(tag)
                        }
                        onRemove={tag.isSystem ? undefined : () => deleteCatalogTag(tag)}
                      />
                    ))}
                    {catalog.length === 0 && (
                      <span className='text-muted fs-7'>No tags in catalog yet</span>
                    )}
                    {hasMoreExisting && (
                      <button
                        ref={moreBtnRef}
                        type='button'
                        className='btn btn-sm btn-light py-0 px-2'
                        style={{
                          borderRadius: PILL_RADIUS,
                          minHeight: 22,
                          fontSize: '0.7rem',
                          border: '1px solid rgba(0,0,0,0.12)',
                          backgroundColor: '#F5F5F5',
                        }}
                        onClick={() => setShowMoreTags((v) => !v)}
                        aria-label='Show more tags'
                        title='More tags'
                      >
                        <i className='bi bi-three-dots'></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Group B: Create Tag */}
              <div
                className='p-3 rounded'
                style={{backgroundColor: '#FFFFFF', border: '1px solid #E0E3E8'}}
              >
                <label className='form-label fs-7 mb-2 fw-semibold text-muted text-uppercase'>
                  Create Tag
                </label>
                <div className='d-flex align-items-center gap-2'>
                  <input
                    ref={newTagInputRef}
                    type='text'
                    className='form-control form-control-sm'
                    placeholder='New tag name'
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onMouseDown={(e) => {
                      e.stopPropagation()
                      newTagInputRef.current?.focus()
                    }}
                    maxLength={100}
                    disabled={saving}
                    style={{minWidth: 0}}
                  />
                  <CallTagColorPicker
                    value={newTagColor}
                    onChange={setNewTagColor}
                    disabled={saving}
                    placement='top'
                  />
                  <button
                    type='button'
                    className='btn btn-sm text-nowrap text-white'
                    onClick={() => void createAndMaybeAssign()}
                    disabled={saving || !newTagName.trim() || nameExists}
                    style={{
                      backgroundColor: '#a3a242',
                      borderColor: '#a3a242',
                      color: '#FFFFFF',
                      opacity: saving || !newTagName.trim() || nameExists ? 0.55 : 1,
                    }}
                  >
                    <i className='bi bi-plus me-1' style={{color: '#FFFFFF'}}></i>
                    <span style={{color: '#FFFFFF'}}>Add Tag</span>
                  </button>
                </div>
                {nameExists && (
                  <small className='text-danger d-block mt-2'>Tag name already exists</small>
                )}
              </div>
            </div>
          )}
        </div>

        <Select
          isMulti
          isSearchable
          classNamePrefix='react-select'
          options={options}
          value={selectedOptions}
          onChange={(opts: readonly TagOption[] | null) =>
            handleSelectChange(opts as TagOption[] | null)
          }
          placeholder='Search and select tags'
          isOptionDisabled={(opt: TagOption) =>
            assigned.length >= MAX_TAGS_PER_CALL && !assigned.some((a) => a.id === opt.value)
          }
          formatOptionLabel={formatOptionLabel}
          isDisabled={saving}
          menuPortalTarget={document.body}
          menuPosition='fixed'
          menuPlacement='top'
          styles={selectPillStyles}
        />
        <small className='text-muted'>Max {MAX_TAGS_PER_CALL} tags per call</small>
      </div>

      <Overlay
        show={showMoreTags && showManage}
        target={moreBtnRef.current}
        placement='top'
        container={document.body}
        rootClose
        onHide={() => setShowMoreTags(false)}
      >
        <Popover
          id='call-tags-more-popover'
          className='call-tags-more-picker shadow'
          style={{maxWidth: 340, zIndex: 2100}}
        >
          <Popover.Header as='div' className='py-2 d-flex justify-content-between align-items-center'>
            <span className='fw-semibold fs-7'>All tags</span>
            <button
              type='button'
              className='btn btn-icon btn-sm'
              onClick={() => setShowMoreTags(false)}
              aria-label='Close'
            >
              ×
            </button>
          </Popover.Header>
          <Popover.Body className='p-2'>
            <input
              type='text'
              className='form-control form-control-sm mb-2'
              placeholder='Search tags…'
              value={moreSearch}
              onChange={(e) => setMoreSearch(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
            />
            <div style={{maxHeight: 220, overflowY: 'auto'}} className='d-flex flex-wrap gap-1 p-1'>
              {filteredMoreTags.map((tag) => (
                <TagPill
                  key={tag.id}
                  tag={tag}
                  compact
                  removeIcon='trash'
                  disabled={saving}
                  onAssign={assignedIds.has(tag.id) ? undefined : () => assignExistingTag(tag)}
                  onRemove={tag.isSystem ? undefined : () => deleteCatalogTag(tag)}
                />
              ))}
              {filteredMoreTags.length === 0 && (
                <span className='text-muted fs-8'>No matching tags</span>
              )}
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  )
}

export function CallTagBadges({tags}: {tags: CallTag[]}) {
  if (!tags?.length) return null
  return (
    <div className='d-flex flex-wrap align-items-center gap-1 ms-3'>
      {tags.map((tag) => {
        const bg = normalizeHex(tag.color)
        const fg = contrastTextForHex(bg)
        return (
          <span
            key={tag.id}
            style={{
              backgroundColor: bg,
              color: fg,
              borderRadius: PILL_RADIUS,
              border: '1px solid rgba(0,0,0,0.12)',
              padding: '2px 8px',
              fontSize: '0.735rem',
              fontWeight: 600,
            }}
          >
            {tag.name}
          </span>
        )
      })}
    </div>
  )
}

export default CallTagsSection
